import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Card,
  CardContent,
  CardActions,
  Typography,
  Chip,
  Alert,
  Snackbar,
} from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ImageIcon from '@mui/icons-material/Image';
import VideoLibraryIcon from '@mui/icons-material/VideoLibrary';
import UpdateIcon from '@mui/icons-material/Update';
import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import type { PlaywrightTest } from '../PlaywrightTests';

interface PlaywrightTestDetailProps {
  test: PlaywrightTest;
  onBack: () => void;
  onRefresh: () => void;
}

const getStatusColor = (status: PlaywrightTest['status']): 'success' | 'error' | 'warning' | 'default' => {
  switch (status) {
    case 'passed':
      return 'success';
    case 'failed':
      return 'error';
    case 'skipped':
      return 'warning';
    default:
      return 'default';
  }
};

const isSnapshotFailure = (error?: string): boolean => {
  if (!error) return false;
  return error.includes('snapshot') || error.includes('toMatchSnapshot') || error.includes('Screenshot comparison');
};

export const PlaywrightTestDetail: React.FC<PlaywrightTestDetailProps> = ({
  test,
  onBack,
  onRefresh,
}) => {
  const [isUpdatingSnapshots, setIsUpdatingSnapshots] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState<string | null>(null);

  const handleUpdateSnapshots = async () => {
    setIsUpdatingSnapshots(true);
    try {
      const res = await fetch('http://localhost:9999/api/playwright-update-snapshots', {
        method: 'POST',
      });
      const result = await res.json();

      if (result.success) {
        setSnackbarMessage('Snapshots updated successfully!');
        setTimeout(() => onRefresh(), 2000);
      } else {
        setSnackbarMessage('Failed to update snapshots. Check console for details.');
        console.error('Snapshot update output:', result.output);
      }
    } catch (error: any) {
      setSnackbarMessage(`Error: ${error.message}`);
    } finally {
      setIsUpdatingSnapshots(false);
    }
  };

  const handleFixWithClaude = () => {
    // TODO: Integrate with FixWithClaudeDialog component
    const prompt = `Fix this failing Playwright test:

Test: ${test.name}
File: ${test.file}
Role: ${test.role}
Tags: ${test.tags.join(', ')}

Error:
${test.error}

Please analyze the error and suggest a fix for this E2E test.`;

    console.log('Claude fix prompt:', prompt);
    setSnackbarMessage('Claude fix integration coming soon!');
  };

  const hasArtifacts = test.artifacts && (
    test.artifacts.screenshots.length > 0 ||
    test.artifacts.videos.length > 0
  );

  return (
    <Box>
      <Button onClick={onBack} startIcon={<ArrowBackIcon />} sx={{ mb: 2 }}>
        Back to Tests
      </Button>

      <Card>
        <CardContent>
          <Typography variant="h5" gutterBottom>
            {test.name}
          </Typography>

          <Typography variant="body2" color="text.secondary" gutterBottom>
            {test.file}
          </Typography>

          <Box mt={2} display="flex" gap={1} flexWrap="wrap">
            <Chip label={test.status.toUpperCase()} color={getStatusColor(test.status)} />
            <Chip label={test.role} variant="outlined" />
            {test.tags.map(tag => (
              <Chip key={tag} label={`@${tag}`} variant="outlined" size="small" />
            ))}
            {test.duration !== undefined && (
              <Chip
                label={`${Math.round(test.duration)}ms`}
                variant="outlined"
                size="small"
              />
            )}
          </Box>

          {test.error && (
            <Alert severity="error" sx={{ mt: 3 }}>
              <Typography variant="subtitle2" gutterBottom>
                Error Message:
              </Typography>
              <Box
                component="pre"
                sx={{
                  mt: 1,
                  p: 2,
                  bgcolor: '#1e1e1e',
                  color: '#d4d4d4',
                  borderRadius: 1,
                  overflow: 'auto',
                  fontSize: '12px',
                  fontFamily: 'monospace',
                  maxHeight: 400,
                }}
              >
                {test.error}
              </Box>
            </Alert>
          )}

          {test.status === 'passed' && (
            <Alert severity="success" sx={{ mt: 3 }}>
              <Typography variant="body2">
                This test passed successfully!
              </Typography>
            </Alert>
          )}
        </CardContent>

        {test.status === 'failed' && (
          <CardActions sx={{ p: 2, pt: 0, gap: 1 }}>
            {hasArtifacts && test.artifacts!.screenshots.length > 0 && (
              <Button
                startIcon={<ImageIcon />}
                variant="outlined"
                size="small"
                disabled
              >
                View Screenshots ({test.artifacts!.screenshots.length})
              </Button>
            )}

            {hasArtifacts && test.artifacts!.videos.length > 0 && (
              <Button
                startIcon={<VideoLibraryIcon />}
                variant="outlined"
                size="small"
                disabled
              >
                View Videos ({test.artifacts!.videos.length})
              </Button>
            )}

            {isSnapshotFailure(test.error) && (
              <Button
                startIcon={<UpdateIcon />}
                variant="outlined"
                color="warning"
                size="small"
                onClick={handleUpdateSnapshots}
                disabled={isUpdatingSnapshots}
              >
                {isUpdatingSnapshots ? 'Updating...' : 'Update Snapshots'}
              </Button>
            )}

            <Button
              startIcon={<AutoFixHighIcon />}
              variant="contained"
              color="primary"
              size="small"
              onClick={handleFixWithClaude}
            >
              Fix with Claude
            </Button>
          </CardActions>
        )}
      </Card>

      <Snackbar
        open={!!snackbarMessage}
        autoHideDuration={6000}
        onClose={() => setSnackbarMessage(null)}
        message={snackbarMessage}
      />
    </Box>
  );
};
